import axios from "axios";
import { AKUA_API } from "../config";

/**
 * Servicio para integración con Akua Payment Gateway
 *
 * Proporciona funcionalidades para:
 * - Obtener listado de transacciones/pagos usando API Key
 * - Procesar devoluciones
 * - Crear links de pago
 */
class AkuaService {
  constructor() {
    this.baseURL = AKUA_API.baseURL;
    this.apiKey = AKUA_API.apiKey;
    this.paymentId = AKUA_API.paymentId;
  }

  /**
   * Crear instancia de axios con configuración base para Akua
   */
  createAxiosInstance() {
    return axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": this.apiKey,
        "x-emid": "mer-d1fbsmbkkjq77lasb9b0",
      },
      timeout: 30000,
    });
  }

  /**
   * Obtener pagos/transacciones con filtros
   * @param {Object} filters - Filtros para buscar pagos
   * @param {string} filters.status - Estado del pago
   * @param {string} filters.created_at_from - Fecha desde
   * @param {string} filters.created_at_to - Fecha hasta
   * @param {number} filters.page - Número de página
   * @param {number} filters.page_size - Tamaño de página
   * @param {string} filters.sort - Ordenamiento (ej: "created_at:desc")
   */
  async getPayments(filters = {}) {
    try {
      const akuaAxios = this.createAxiosInstance();

      // Construir parámetros de query
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      console.log("🔍 Obteniendo pagos de Akua...");
      const response = await akuaAxios.get(`/v1/payments?${params.toString()}`);

      console.log("✅ Pagos obtenidos exitosamente");
      return response.data;
    } catch (error) {
      console.error("❌ Error obteniendo pagos de Akua:", error);

      // Detectar errores de CORS específicamente
      if (error.code === "ERR_NETWORK" || error.message.includes("CORS") || error.message.includes("Access-Control-Allow-Origin")) {
        console.warn("🚫 Error de CORS detectado - La API de Akua no permite peticiones desde este dominio");
        throw new Error("Error de CORS: La API de Akua no permite peticiones desde este dominio. Contacta al administrador de Akua para configurar CORS.");
      }

      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        throw new Error(`Error de API: ${error.response.status} - ${error.response.data?.message || "Error desconocido"}`);
      } else if (error.request) {
        console.error("No se recibió respuesta del servidor");
        throw new Error("Error de red: No se pudo conectar con la API de Akua. Verifica tu conexión a internet.");
      } else {
        throw new Error(`Error de configuración: ${error.message}`);
      }
    }
  }

  /**
   * Obtener detalles de un pago específico
   * @param {string} paymentId - ID del pago
   */
  async getPaymentById(paymentId) {
    try {
      const akuaAxios = this.createAxiosInstance();

      console.log(`🔍 Obteniendo pago ${paymentId}...`);
      const response = await akuaAxios.get(`/v1/payments/${paymentId}`);

      console.log("✅ Pago obtenido exitosamente");
      return response.data;
    } catch (error) {
      console.error(`❌ Error obteniendo pago ${paymentId}:`, error);
      if (error.response) {
        throw new Error(`Error ${error.response.status}: ${error.response.data?.message || "Error en la API de Akua"}`);
      }
      throw new Error("Error de conexión con Akua API");
    }
  }

  /**
   * Procesar devolución o cancelación
   * @param {string} paymentId - ID del pago
   * @param {Object} refundData - Datos de la devolución
   * @param {number} refundData.amount - Monto a devolver (opcional, si no se especifica se devuelve todo)
   * @param {string} refundData.reason - Razón de la devolución
   */
  async cancelOrRefund(paymentId, refundData = {}) {
    try {
      const akuaAxios = this.createAxiosInstance();

      // El payload no incluye el ID del pago ya que va en la URL
      const payload = {
        ...refundData,
      };

      console.log(`🔄 Procesando devolución para pago ${paymentId}...`);
      const response = await akuaAxios.post(`/v1/payments/${paymentId}/cancel-or-refund`, payload);

      console.log("✅ Devolución procesada exitosamente");
      return response.data;
    } catch (error) {
      console.error(`❌ Error procesando devolución para pago ${paymentId}:`, error);
      if (error.response) {
        throw new Error(`Error ${error.response.status}: ${error.response.data?.message || "Error procesando devolución"}`);
      }
      throw new Error("Error de conexión con Akua API");
    }
  }

  /**
   * Crear link de pago usando la API /v1/links
   * @param {Object} paymentData - Datos del pago
   * @param {string} paymentData.type - Tipo de link (ej: "STATIC")
   * @param {Object} paymentData.amount - Objeto con value y currency
   * @param {number} paymentData.amount.value - Monto del pago
   * @param {string} paymentData.amount.currency - Moneda (ej: "USD", "COP")
   * @param {string} paymentData.redirect_url - URL de redirección exitosa
   * @param {string} paymentData.cancel_url - URL de redirección en caso de cancelación
   * @param {string} paymentData.merchant_id - ID del comercio
   * @param {string} paymentData.description - Descripción del pago
   * @param {number} paymentData.expires_in - Tiempo de expiración en segundos (default: 3600)
   * @param {Object} paymentData.metadata - Metadatos adicionales
   */
  async createPaymentLink(paymentData) {
    try {
      const akuaAxios = this.createAxiosInstance();

      // Construir el payload según la documentación de Akua
      const payload = {
        type: paymentData.type || "STATIC",
        amount: {
          value: paymentData.amount.value,
          currency: paymentData.amount.currency,
        },
        redirect_url: paymentData.redirect_url,
        cancel_url: paymentData.cancel_url,
        merchant_id: paymentData.merchant_id,
        description: paymentData.description,
        expires_in: paymentData.expires_in || 3600,
        ...(paymentData.metadata && { metadata: paymentData.metadata }),
      };

      console.log("🔗 Creando link de pago con Akua API v1/links...");
      const response = await akuaAxios.post("/v1/links", payload);

      console.log("✅ Link de pago creado exitosamente");
      return response.data;
    } catch (error) {
      console.error("❌ Error creando link de pago:", error);
      if (error.response) {
        throw new Error(`Error ${error.response.status}: ${error.response.data?.message || "Error creando link de pago"}`);
      }
      throw new Error("Error de conexión con Akua API");
    }
  }

  /**
   * Mapear estados de Akua a estados legibles
   * @param {string} status - Estado de Akua
   */
  mapPaymentStatus(status) {
    const statusMap = {
      CREATED: "Creado",
      AUTHORIZED: "Autorizado",
      PREAUTHORIZED: "Pre-autorizado",
      REJECTED: "Rechazado",
      CANCELLED: "Cancelado",
      CAPTURE_SCHEDULED: "Captura Programada",
      CAPTURE_IN_PROGRESS: "Captura en Proceso",
      CAPTURE_FAILED: "Captura Fallida",
      CAPTURE_PRESENTED: "Captura Presentada",
      CAPTURED: "Capturado",
      REFUNDED: "Devuelto",
      COMPLETED: "Completado",
    };

    return statusMap[status] || status;
  }

  /**
   * Obtener color para el estado del pago (para UI)
   * @param {string} status - Estado de Akua
   */
  getStatusColor(status) {
    const colorMap = {
      COMPLETED: "success",
      CAPTURED: "success",
      AUTHORIZED: "info",
      PREAUTHORIZED: "info",
      CREATED: "warning",
      CAPTURE_SCHEDULED: "warning",
      CAPTURE_IN_PROGRESS: "warning",
      REJECTED: "error",
      CANCELLED: "error",
      CAPTURE_FAILED: "error",
      REFUNDED: "secondary",
    };

    return colorMap[status] || "default";
  }
}

// Exportar una instancia singleton
const akuaService = new AkuaService();
export default akuaService;
